<script setup lang="ts">
import type { PillProps } from '../types/Pill';
import Icon from './Icon.vue';

const props = withDefaults(defineProps<PillProps>(), {
	severity: 'basic',
	size: 'md',
	iconPosition: 'start',
});
</script>

<template>
	<component
		class="pill"
		:class="[{
			[`pill--${props.severity}`]: props.severity,
			[`pill--${props.excellence?.toLowerCase()}`]: props.excellence,
			[`pill--${props.size}`]: props.size,
		}]"
		:type="props.isClickable ? 'button' : undefined"
		:is="props.isClickable ? 'button' : 'span'"
	>
		<Icon :name="props.icon" v-if="props.icon && props.iconPosition === 'start'" />
		<span class="pill__text">{{ props.label }}</span>
		<Icon :name="props.icon" v-if="props.icon && props.iconPosition === 'end'" />
	</component>
</template>

<style scoped lang="scss">
@layer hm-components
{
	.pill
	{
		flex-shrink:0;
		@include flex(null, center, 4, true);
		border:1px solid transparent;
		border-radius:$radius-full;
		position:relative;

		&:is(button)
		{
			cursor:pointer;
			transition:var(--transition-fast);

			@include hover
			{
				opacity:0.64;
			}
		}

		&__text
		{
			@include text-caption;
			white-space:nowrap;
		}

		&--basic
		{
			color:var(--text-basic-600);
			border-color:var(--bg-basic-400);
			background-color:var(--bg-basic-0);
		}
		&--primary
		{
			color:var(--text-basic-0);
			background-color:var(--primary-900);
		}
		&--success
		{
			color:var(--text-basic-900);
			background-color:color-opacity(var(--other-success), 32);
		}
		&--warning
		{
			color:var(--text-basic-900);
			background-color:color-opacity(var(--other-warning), 32);
		}
		&--error
		{
			color:var(--text-basic-900);
			background-color:color-opacity(var(--other-error), 32);
		}
		&--info
		{
			color:var(--text-basic-900);
			background-color:color-opacity(var(--other-info), 32);
		}

		&--hcircle
		{
			color:var(--text-basic-0);
			border-color:transparent;
			background-color:var(--excellence-h);
		}
		&--platinum
		{
			color:var(--text-basic-0);
			border-color:transparent;
			background-color:var(--excellence-platinum);
		}
		&--gold
		{
			color:var(--text-basic-0);
			border-color:transparent;
			background-color:var(--excellence-gold);
		}
		&--presidentclub
		{
			color:var(--text-basic-950);
			border-color:transparent;
			background-color:var(--excellence-president-club);
		}

		&--sm
		{
			padding:$spacing-4xs $spacing-2xs;

			&:deep(.icon)
			{
				@include svg(12);
			}
		}
		&--md
		{
			padding:$spacing-3xs $spacing-xs;

			&:deep(.icon)
			{
				@include svg(16);
			}
		}
	}
}
</style>
